import React, { useEffect, useState } from 'react';
import { Calendar, MapPin, Clock, Info, Heart, Sparkles } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { weddingData } from '../mockData';

const PracticalInfo = () => {
  const { language } = useLanguage();
  const content = weddingData[language].practicalInfo;
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  return (
    <div className="min-h-screen pt-24 pb-20">
      {/* Ultra Premium Header */}
      <section className="bg-gradient-to-br from-lilac-600 via-lilac-500 to-lilac-700 py-32 relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 bg-gold-400 rounded-full filter blur-3xl animate-float" />
          <div className="absolute bottom-20 right-20 w-64 h-64 bg-lilac-300 rounded-full filter blur-3xl animate-float" style={{ animationDelay: '2s' }} />
        </div>

        <div
          className={`container mx-auto px-6 sm:px-8 lg:px-12 text-center relative z-10 transition-all duration-1000 transform ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <Info className="mx-auto text-gold-400 mb-8" size={64} />
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-playfair font-bold text-white mb-6">
            {content.title}
          </h1>
          <div className="w-24 h-1 bg-gradient-to-r from-transparent via-gold-300 to-transparent mx-auto rounded-full" />
        </div>
      </section>

      {/* Events Timeline - Premium Design */}
      <section className="py-32 bg-white relative">
        <div className="container mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-20">
            <Sparkles className="mx-auto text-gold-500 mb-6" size={48} />
            <h2 className="text-4xl sm:text-5xl font-playfair font-bold text-lilac-700 mb-6">
              {content.events.title}
            </h2>
            <div className="divider-gold" />
          </div>

          {/* Event Cards - 3 Cards Grid */}
          <div className="max-w-6xl mx-auto space-y-16">
            {/* Row 1: Civil & Religious */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              {/* Civil Wedding */}
              <div className="card-premium hover-lift group">
                <div className="text-center">
                  <div className="w-24 h-24 mx-auto mb-8 bg-gradient-to-br from-gold-100 to-gold-50 rounded-full flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-500">
                    <Calendar className="text-gold-600 group-hover:text-lilac-600 transition-colors duration-500" size={48} />
                  </div>
                  <h3 className="text-3xl font-playfair font-bold text-gold-700 mb-6 group-hover:text-lilac-600 transition-colors duration-500">
                    {content.events.civil.title}
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-center space-x-3 text-gray-700">
                      <Calendar size={20} className="text-gold-500" />
                      <span className="font-montserrat text-base font-medium">{content.events.civil.date}</span>
                    </div>
                    <div className="flex items-center justify-center space-x-3 text-gray-600">
                      <Clock size={20} className="text-gold-400" />
                      <span className="font-montserrat text-sm">{content.events.civil.time}</span>
                    </div>
                    <div className="flex items-start justify-center space-x-3 text-gray-600 mt-4 pt-4 border-t border-gray-100">
                      <MapPin size={20} className="text-gold-500 flex-shrink-0 mt-0.5" />
                      <span className="font-montserrat text-sm">{content.events.civil.location}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Religious Ceremony */}
              <div className="card-premium hover-lift group bg-gradient-to-br from-lilac-50/50 to-white">
                <div className="text-center">
                  <div className="w-24 h-24 mx-auto mb-8 bg-gradient-to-br from-lilac-100 to-lilac-50 rounded-full flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-500">
                    <Heart className="text-lilac-600 group-hover:text-gold-600 fill-lilac-600 group-hover:fill-gold-600 transition-all duration-500" size={48} />
                  </div>
                  <h3 className="text-3xl font-playfair font-bold text-lilac-700 mb-6 group-hover:text-gold-600 transition-colors duration-500">
                    {content.events.religious.title}
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-center space-x-3 text-gray-700">
                      <Calendar size={20} className="text-gold-500" />
                      <span className="font-montserrat text-base font-medium">{content.events.religious.date}</span>
                    </div>
                    <div className="flex items-center justify-center space-x-3 text-gray-600">
                      <Clock size={20} className="text-lilac-400" />
                      <span className="font-montserrat text-sm">{content.events.religious.time}</span>
                    </div>
                    <div className="flex items-start justify-center space-x-3 text-gray-600 mt-4 pt-4 border-t border-gray-100">
                      <MapPin size={20} className="text-gold-500 flex-shrink-0 mt-0.5" />
                      <span className="font-montserrat text-sm leading-relaxed">{content.events.religious.location}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Row 2: Reception */}
            <div className="flex justify-center">
              <div className="card-premium hover-lift group bg-gradient-to-br from-gold-50/50 to-white max-w-md">
                <div className="text-center">
                  <div className="w-24 h-24 mx-auto mb-8 bg-gradient-to-br from-gold-100 to-gold-50 rounded-full flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-500">
                    <Sparkles className="text-gold-600 group-hover:text-lilac-600 transition-colors duration-500" size={48} />
                  </div>
                  <h3 className="text-3xl font-playfair font-bold text-gold-700 mb-6 group-hover:text-lilac-600 transition-colors duration-500">
                    {content.events.reception.title}
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-center space-x-3 text-gray-700">
                      <Calendar size={20} className="text-gold-500" />
                      <span className="font-montserrat text-base font-medium">{content.events.reception.date}</span>
                    </div>
                    <div className="flex items-center justify-center space-x-3 text-gray-600">
                      <Clock size={20} className="text-gold-400" />
                      <span className="font-montserrat text-sm">{content.events.reception.time}</span>
                    </div>
                    <div className="flex items-start justify-center space-x-3 text-gray-600 mt-4 pt-4 border-t border-gray-100">
                      <MapPin size={20} className="text-gold-500 flex-shrink-0 mt-0.5" />
                      <span className="font-montserrat text-sm font-semibold">{content.events.reception.location}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section - Premium */}
      <section className="py-24 bg-gradient-to-br from-lilac-50 via-warm-white to-cream">
        <div className="container mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <MapPin className="mx-auto text-gold-500 mb-6" size={48} />
              <h2 className="text-4xl font-playfair font-bold text-lilac-700 mb-4">
                {language === 'fr' ? 'Lieux des Cérémonies' : 'Ceremony Venues'}
              </h2>
              <div className="divider-lilac" />
            </div>

            {/* Map Placeholder - Premium Card */}
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border-2 border-lilac-100">
              <div className="aspect-video bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center relative">
                <div className="text-center p-12 relative z-10">
                  <MapPin className="mx-auto text-lilac-400 mb-6 animate-pulse" size={64} />
                  <h3 className="text-2xl font-playfair font-bold text-gray-800 mb-3">
                    Paroisse Sainte Cécile des 2 Plateaux Vallon
                  </h3>
                  <p className="text-gold-600 font-montserrat text-lg font-medium mb-2">
                    Abidjan, Côte d'Ivoire
                  </p>
                  <div className="w-20 h-1 bg-gradient-to-r from-transparent via-lilac-400 to-transparent mx-auto rounded-full my-4" />
                  <p className="text-gray-500 font-montserrat text-base italic">
                    {language === 'fr' ? 'Carte interactive à venir' : 'Interactive map coming soon'}
                  </p>
                </div>
                
                {/* Decorative corner elements */}
                <div className="absolute top-4 left-4 w-16 h-16 border-t-2 border-l-2 border-lilac-200 rounded-tl-2xl" />
                <div className="absolute bottom-4 right-4 w-16 h-16 border-b-2 border-r-2 border-gold-200 rounded-br-2xl" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Info - Premium Grid */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Dress Code */}
            <div className="card-premium hover-lift">
              <h3 className="text-3xl font-playfair font-bold text-lilac-700 mb-6">
                {content.dressCode.title}
              </h3>
              <p className="text-gray-700 font-montserrat leading-relaxed text-lg mb-8">
                {content.dressCode.description}
              </p>
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-lilac-400 to-lilac-600 shadow-lg" />
                <div className="w-16 h-16 rounded-full bg-white border-4 border-gray-200 shadow-lg" />
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-gold-400 to-gold-600 shadow-lg" />
              </div>
            </div>

            {/* Contact */}
            <div className="card-premium hover-lift bg-gradient-to-br from-gold-50/30 to-white">
              <h3 className="text-3xl font-playfair font-bold text-gold-700 mb-6">
                {content.contact.title}
              </h3>
              <p className="text-gray-700 font-montserrat leading-relaxed text-lg mb-8">
                {content.contact.description}
              </p>
              <a
                href="mailto:cyrilleneninbi@gmail.com"
                className="inline-block px-8 py-4 bg-gradient-to-r from-gold-500 to-gold-600 text-white rounded-full font-montserrat font-bold shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-500"
              >
                {language === 'fr' ? 'Nous contacter' : 'Contact us'}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Planning - Premium */}
      <section className="py-24 bg-gradient-to-br from-cream via-lilac-50/30 to-warm-white">
        <div className="container mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <div className="glass-card rounded-3xl p-12 sm:p-16 shadow-2xl text-center border-2 border-lilac-100">
              <Clock className="mx-auto text-lilac-500 mb-8" size={56} />
              <h3 className="text-3xl sm:text-4xl font-playfair font-bold text-lilac-700 mb-6">
                {content.planning.title}
              </h3>
              <div className="divider-lilac mb-8" />
              <p className="text-gray-700 font-montserrat leading-relaxed text-lg">
                {content.planning.description}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PracticalInfo;
